import { Trip } from "@/types/trip";
import TripCard from "../TripCard";
import { useRef, useEffect, useState } from "react";
import { Badge } from "../ui/badge";
import { parseISO } from "date-fns";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "@/hooks/use-analytics"; 
import { useLocation } from "react-router-dom";


interface TimelineEntryProps {
  trip: Trip;
  index: number;
  isTimelineVisible: boolean;
  isFocused?: boolean;
  handleRefreshTrips?: () => void;
}

// Removed TimelineAdCard component as per the new ad placement strategy

// ✅ Fixed TimelineEntry
export const TimelineEntry = ({
  trip,
  index,
  isFocused = false,
  handleRefreshTrips,
}: TimelineEntryProps) => {
  const entryRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  const [cardHeight, setCardHeight] = useState<string>("auto");
  const { t } = useTranslation();
  const { trackIndexAction } = useAnalytics();
  const location = useLocation();

  useEffect(() => {
    if (isFocused && entryRef.current) {
      entryRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "center",
      });
      trackIndexAction("view_trip_details", trip.id, `Destination: ${trip.destination}`);
    }
  }, [isFocused, trip.id, trip.destination, trackIndexAction]);

  useEffect(() => {
    const calculateCardHeight = () => {
      const height = window.innerHeight * 0.8;
      setCardHeight(`${height}px`);
    };
    calculateCardHeight();
    window.addEventListener("resize", calculateCardHeight);
    return () => {
      window.removeEventListener("resize", calculateCardHeight);
    };
  }, [isMobile]);


  const now = new Date();

  const hasStarted = trip.elements.some((element) => {
    const startDate = parseISO(element.startDate || "1970");
    return startDate <= now;
  });

  const hasFutureSegments = trip.elements.some((element) => {
    const endDate = parseISO(element.endDate || "1970");
    return endDate >= now;
  });

  const isOngoing = hasStarted && hasFutureSegments;
  const isUpcoming = !hasStarted && hasFutureSegments;

  const badgeText = isOngoing
    ? t("tripCard.tripStatus.ongoing")
    : isUpcoming
    ? t("tripCard.tripStatus.upcoming")
    : t("tripCard.tripStatus.past");

  const badgeColor = isOngoing
    ? "bg-travel-100 text-travel-800"
    : isUpcoming
    ? "bg-travel-100 text-travel-800"
    : "bg-gray-100 text-gray-600";

  return (
    <div
      ref={entryRef}
      key={trip.id}
      className="relative flex flex-col justify-center min-w-[320px] max-w-full px-2 mt-8"
      style={{
        height: cardHeight,
        overflow: "auto",
        paddingTop: "12px",
        paddingBottom: "40px",
      }}
    >
      <div className="flex justify-center h-full relative">
        <div className="relative">
          <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-20">
            <Badge
              variant="secondary"
              className={`${badgeColor} border border-travel-200 px-3 py-1 text-xs font-medium shadow-sm ${
                !isOngoing && !isUpcoming ? "opacity-70" : ""
              }`}
            >
              {badgeText}
            </Badge>
          </div>
          <TripCard trip={trip} isFocused={isFocused} handleRefreshTrips={handleRefreshTrips} />
        </div>
      </div>   
    </div>
  );
};
